import React from 'react';
import calendarService from '../../services/calendarService';

/**
 * CalendarGrid Component
 * Displays events in a monthly calendar grid format
 */
const CalendarGrid = ({
  events,
  currentMonth,
  currentYear,
  onDateClick,
  onEventSponsor,
  theme,
  occasionMap
}) => {
  // Get calendar grid data
  const calendarGrid = calendarService.getCalendarGrid(events, currentYear, currentMonth);
  
  // Day names
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;
  
  // Styles
  const styles = {
    container: {
      width: '100%',
      overflowX: 'auto'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '100%' : '900px'
    },
    dayHeader: {
      padding: '12px 8px',
      textAlign: 'center',
      fontWeight: '600',
      fontSize: isMobile ? '12px' : '14px',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      borderBottom: '2px solid #e2e8f0',
      backgroundColor: '#f8fafc'
    },
    weekRow: {
      borderBottom: '1px solid #e2e8f0'
    },
    dayCell: {
      position: 'relative',
      padding: isMobile ? '4px' : '6px',
      minHeight: isMobile ? '50px' : '90px',
      height: isMobile ? '50px' : 'auto',
      width: '14.28%',
      verticalAlign: 'top',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      backgroundColor: 'white',
      border: '1px solid #e2e8f0'
    },
    dayCellHover: {
      backgroundColor: '#f0f9ff'
    },
    dayCellToday: {
      backgroundColor: '#f0f9ff',
      border: '2px solid #3b82f6'
    },
    dayCellOtherMonth: {
      backgroundColor: '#f8fafc',
      color: '#cbd5e0'
    },
    dayCellPast: {
      backgroundColor: '#fafafa',
      opacity: 0.7
    },
    dayNumber: {
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '500',
      marginBottom: '2px',
      color: theme?.colors?.cardText || '#333'
    },
    dayNumberToday: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '20px',
      height: '20px',
      backgroundColor: '#3b82f6',
      color: 'white',
      borderRadius: '50%',
      fontWeight: '600',
      fontSize: isMobile ? '11px' : '12px'
    },
    eventContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      marginTop: '4px'
    },
    eventItem: {
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: isMobile ? '10px' : '11px',
      lineHeight: '1.2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      transition: 'opacity 0.2s',
      color: 'white',
      fontWeight: '500'
    },
    eventDot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: '4px'
    },
    moreEvents: {
      fontSize: '10px',
      color: theme?.colors?.primary || '#3b82f6',
      fontWeight: '600',
      marginTop: '2px',
      cursor: 'pointer'
    },
    fridayCell: {
      backgroundColor: '#f0f9ff' // Light sky blue — distinct from joyous green and mourning gray
    },
    weekendCell: {
      backgroundColor: 'white'
    }
  };

  // Render event dots instead of text
  const renderEventDots = (events) => {
    const maxDotsToShow = 5;
    const eventsToShow = events.slice(0, maxDotsToShow);
    
    return (
      <div style={{ 
        display: 'flex', 
        gap: '2px', 
        flexWrap: 'wrap',
        marginTop: '2px',
        justifyContent: 'center'
      }}>
        {eventsToShow.map((event, index) => (
          <div
            key={event.id}
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: event.color || theme?.colors?.primary || '#3b82f6',
              border: '1px solid rgba(0,0,0,0.1)'
            }}
            title={`${event.displayTime} - ${event.title}`}
          />
        ))}
        {events.length > maxDotsToShow && (
          <div style={{ 
            fontSize: '9px', 
            color: theme?.colors?.cardTextSecondary || '#64748b',
            marginLeft: '2px'
          }}>
            +{events.length - maxDotsToShow}
          </div>
        )}
      </div>
    );
  };

  // Render day cell
  const renderDayCell = (day) => {
    // Parse date string as local date to avoid timezone issues
    // Adding 'T00:00:00' ensures it's interpreted as local time, not UTC
    const localDate = new Date(day.date + 'T00:00:00');
    const dayOfWeek = localDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday (0) or Saturday (6)
    const isFriday = dayOfWeek === 5;
    
    // Check for Islamic occasion on this date (for cell background highlight)
    const occasionData = occasionMap && day.isCurrentMonth ? occasionMap.get(day.date) : null;
    const hasOccasion = occasionData?.occasions?.length > 0;
    const occasionType = hasOccasion ? occasionData.occasions[0].type : null;

    // Occasion cell background — light gray for mourning, light green for joyous
    // (green = celebration in Islam), light blue for significant
    const occasionCellStyle = hasOccasion ? {
      backgroundColor: occasionType === 'mourning' ? '#787878'
        : occasionType === 'joyous' ? '#f0fdf4'
        : '#eff6ff',
      borderColor: occasionType === 'mourning' ? '#999'
        : occasionType === 'joyous' ? '#86efac'
        : '#93c5fd',
    } : {};

    // Apply styles in correct order — occasion highlight takes priority over day-of-week colors
    const cellStyle = {
      ...styles.dayCell,
      ...(day.isToday ? styles.dayCellToday : {}),
      ...(!day.isCurrentMonth ? styles.dayCellOtherMonth : {}),
      ...(day.isPast ? styles.dayCellPast : {}),
      ...(isFriday && day.isCurrentMonth ? styles.fridayCell : {}),
      ...(isWeekend && day.isCurrentMonth ? styles.weekendCell : {}),
      ...(hasOccasion ? occasionCellStyle : {}), // Occasion highlight overrides day-of-week
    };
    
    return (
      <td
        key={day.date}
        style={cellStyle}
        onClick={() => onDateClick && onDateClick(day.date, day.events)}
        onMouseEnter={(e) => {
          if (!day.isToday && day.isCurrentMonth) {
            e.currentTarget.style.backgroundColor = '#f0f9ff';
            e.currentTarget.style.cursor = day.events.length > 0 ? 'pointer' : 'default';
          }
        }}
        onMouseLeave={(e) => {
          if (!day.isToday && day.isCurrentMonth) {
            // Restore occasion highlight if applicable
            if (hasOccasion) {
              e.currentTarget.style.backgroundColor = occasionCellStyle.backgroundColor;
            } else {
              const localDate = new Date(day.date + 'T00:00:00');
              const dayOfWeek = localDate.getDay();
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const isFriday = dayOfWeek === 5;

              if (day.isPast) {
                e.currentTarget.style.backgroundColor = '#fafafa';
              } else if (isFriday) {
                e.currentTarget.style.backgroundColor = '#f0f9ff';
              } else {
                e.currentTarget.style.backgroundColor = 'white';
              }
            }
          }
        }}
        title={day.events.length > 0 ? `${day.events.length} event${day.events.length > 1 ? 's' : ''} - Click to see details` : ''}
      >
        <div style={{
          ...(day.isToday ? styles.dayNumberToday : styles.dayNumber),
          ...(occasionType === 'mourning' && !day.isToday ? { color: '#e0e0e0' } : {}),
        }}>
          {day.dayNumber}
        </div>
        {/* Hijri date + occasion text (cell background already indicates occasion type) */}
        {occasionMap && day.isCurrentMonth && (() => {
          const data = occasionData; // Already computed above for cell styling
          if (!data?.hijri) return null;
          // Don't show Hijri dates for future unconfirmed months
          if (data.isFutureUnconfirmed) return null;

          const { hijri, occasions } = data;
          const isNewMonth = hijri.day === 1;
          const isMourning = occasionType === 'mourning';
          const isLastDay = data.isLastDayOfMonth;

          return (
            <>
              {/* Hijri date + occasion emoji (inline, top-right) */}
              <div style={{
                position: 'absolute',
                top: isMobile ? '2px' : '6px',
                right: isMobile ? '3px' : '8px',
                fontSize: isMobile ? '7px' : '10px',
                fontWeight: isNewMonth ? 600 : 400,
                color: isMourning
                  ? '#ccc'
                  : isNewMonth
                    ? (theme?.colors?.primary || '#1A5632')
                    : (theme?.colors?.cardTextSecondary || '#94a3b8'),
                opacity: hasOccasion ? 1 : (isNewMonth ? 1 : (isMourning ? 0.9 : 0.5)),
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
              }}
                title={hasOccasion ? occasions[0].nameEn
                  : isLastDay ? 'Last day of the month — next month depends on moon sighting'
                  : ''}
              >
                {hasOccasion && (
                  <span style={{ fontSize: isMobile ? '8px' : '11px' }}>
                    {occasionType === 'mourning' ? '🏴' : occasionType === 'joyous' ? '🎉' : '✨'}
                  </span>
                )}
                {isLastDay && !hasOccasion && (
                  <span style={{ fontSize: isMobile ? '8px' : '11px' }}>🌙</span>
                )}
                {isNewMonth
                  ? `1 ${isMobile ? hijri.monthAbbr : hijri.monthName}`
                  : isMobile
                    ? hijri.day
                    : `${hijri.day} ${hijri.monthAbbr}`
                }
              </div>
            </>
          );
        })()}
        {day.events.length > 0 && (
          <>
            {renderEventDots(day.events)}
            <div style={{
              fontSize: '10px',
              color: theme?.colors?.primary || '#3b82f6',
              marginTop: '2px',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              {day.events.length} event{day.events.length > 1 ? 's' : ''}
            </div>
          </>
        )}
      </td>
    );
  };

  return (
    <div style={styles.container} data-calendar-grid="true">
      <table style={styles.table}>
        <thead>
          <tr>
            {(isMobile ? dayNamesShort : dayNames).map(day => (
              <th key={day} style={styles.dayHeader}>
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calendarGrid.map((week, weekIndex) => (
            <tr key={weekIndex} style={styles.weekRow}>
              {week.map(day => renderDayCell(day))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CalendarGrid;